import React, { useState, useEffect } from "react";
import "../styles/ProfileModal.css";
import { useAuth } from "../context/AuthContext";
import { usePayment } from "../context/PaymentContext";
import { FaLock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import countriesData from "../data/countries.json";
import PhoneInput from "./PhoneInput";

const ProfileModal = ({ onClose }) => {
    const { showNotification, logout } = useAuth();
    const { profile, updateProfile, fetchProfile } = usePayment();
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editPassword, setEditPassword] = useState(false);
    const [editedProfile, setEditedProfile] = useState({
        username: "",
        birthdate: "",
        country: "",
        phoneNumber: "",
        email: "",
        timezone: ""
    });

    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const { t } = useTranslation();

    // Обновляем профиль при открытии модального окна
    useEffect(() => {
        fetchProfile();
    }, []);

    useEffect(() => {
        if (profile) {

            // Создаем маппинг из countries.json динамически
            const countryMapping = {};

            // Добавляем специальные случаи Cloudflare
            countryMapping['UK'] = 'GB'; // Cloudflare может возвращать UK вместо GB

            // Создаем обратный маппинг: название → код из countries.json
            countriesData.countries.forEach(country => {
                // Коды остаются как есть
                countryMapping[country.code] = country.code;

                // Английские названия → коды
                countryMapping[country.nameEn] = country.code;

                // Русские названия → коды (на всякий случай)
                countryMapping[country.name] = country.code;
            });

            // Применяем маппинг к стране
            let mappedCountry = profile.country || "";
            if (mappedCountry && countryMapping[mappedCountry]) {
                mappedCountry = countryMapping[mappedCountry];
            }

            // Проверяем, есть ли такая страна в нашем списке
            const countryExists = countriesData.countries.some(country => country.code === mappedCountry);
            if (!countryExists && mappedCountry) {
                // Можно попробовать использовать registration_country как fallback
                const regCountryExists = countriesData.countries.some(country => country.code === profile.registration_country);
                if (regCountryExists) {
                    mappedCountry = profile.registration_country;
                }
            }

            setEditedProfile({
                username: profile.username || "",
                birthdate: profile.birthdate || "",
                country: mappedCountry,
                phoneNumber: profile.phoneNumber || "",
                email: profile.email || "",
                timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
            });
        }
    }, [profile]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setEditedProfile((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            console.log('Отправка данных профиля:', {
                ...editedProfile,
                timezone: editedProfile.timezone
            });
            const result = await updateProfile({
                username: editedProfile.username,
                birthdate: editedProfile.birthdate,
                country: editedProfile.country,
                phone_number: editedProfile.phoneNumber,
                email: editedProfile.email,
                timezone: editedProfile.timezone
            });
            if (result.success) {
                setEditMode(false);
                showNotification("Профиль успешно обновлен", "success");
                fetchProfile();
            } else {
                showNotification(result.message || "Ошибка при обновлении профиля", "error");
            }
        } catch (error) {
            console.error('Ошибка при обновлении профиля:', error);
            showNotification("Произошла  ошибка при обновлении профиля", "error");
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (passwords.newPassword !== passwords.confirmPassword) {
            showNotification("Новые пароли не совпадают", "error");
            setLoading(false);
            return;
        }
        try {
            const result = await updateProfile({
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword,
                confirmPassword: passwords.confirmPassword
            });
            if (result.success) {
                showNotification("Пароль успешно изменен", "success");
                setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
                setEditPassword(false);
            } else {
                showNotification(result.message || "Ошибка при изменении пароля", "error");
            }
        } catch {
            showNotification("Произошла ошибка при смене пароля", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSendEmailVerification = async () => {
        // Проверка наличия email (для аккаунтов через Telegram)
        if (!profile?.email) {
            showNotification('Email не указан. Пожалуйста, добавьте email в настройках профиля', 'error');
            return;
        }
        
        try {
            const response = await fetch(`${require('../utils/config').getApiUrl()}/api/email-verification/send`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await response.json();
            if (response.ok) {
                showNotification(data.message || 'Письмо отправлено!', 'success');
            } else {
                showNotification(data.message || 'Ошибка отправки письма', 'error');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            showNotification('Ошибка при отправке письма', 'error');
        }
    };



    return (
        <div className="profile-modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        }}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <button className="profile-modal-close" onClick={onClose}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#23232e" />
                        <path d="M16 8L8 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        <path d="M8 8L16 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
                <h3 className="profile-modal-title">{t("profileModal.title")}</h3>
                {!editPassword && (
                    <div className="profile-modal-form">

<div className="profile-modal-actions">
                            {editMode ? (
                                <>
                                    <button type="button" className="btn sign-up glow-blue" disabled={loading} onClick={handleSaveProfile}>
                                        {loading ? t("profileModal.saving") : t("profileModal.save")}
                                    </button>
                                    <button type="button" className="btn" onClick={() => setEditMode(false)} disabled={loading}>
                                        {t("profileModal.cancel")}
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="btn sign-up" onClick={() => setEditMode(true)}>
                                    {t("profileModal.editProfile")}
                                </button>
                            )}
                        </div>

                        {/* Кнопка смены аккаунта */}
                        <button
                            className="btn logout"
                            style={{ width: '100%', marginTop: 16, background: 'red' }}
                            onClick={logout}
                        >
                            {t("profile.Change account")}
                        </button>

                        <div className="profile-modal-password-row">
                            <label className="profile-modal-label" style={{ flex: 1 }}>{t("profileModal.password")}
                                <input
                                    type="password"
                                    value={"********"}
                                    disabled
                                    style={{ letterSpacing: 2 }}
                                />
                            </label>
                            {!editPassword && (
                                <button
                                    type="button"
                                    className="profile-modal-edit-btn"
                                    onClick={() => setEditPassword(true)}
                                >
                                    {t("profileModal.changePassword")}
                                </button>
                            )}
                        </div>

                        <div className="profile-modal-field">
                            <span className="profile-modal-field-label">Email</span>
                            <input
                                className="profile-modal-field-input"
                                type="email"
                                name="email"
                                value={editedProfile.email}
                                onChange={handleProfileChange}
                                disabled={!editMode}
                            />
                        </div>
                        {profile?.emailVerified ? (
                            <div className="email-verified-badge">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Email подтвержден
                            </div>
                        ) : profile?.email ? (
                            <button
                                type="button"
                                className="email-verify-btn"
                                onClick={handleSendEmailVerification}
                            >
                                ✉️ Подтвердить email
                            </button>
                        ) : (
                            <div className="email-not-set-hint">
                                Добавьте email, чтобы получить дополнительную защиту аккаунта
                            </div>
                        )}

                        <div className="profile-modal-field">
                            <span className="profile-modal-field-label">{t("profileModal.nickname")}</span>
                            <input
                                className="profile-modal-field-input"
                                type="text"
                                name="username"
                                value={editedProfile.username}
                                onChange={handleProfileChange}
                                disabled={!editMode}
                            />
                        </div>
                        <div className="profile-modal-field">
                            <span className="profile-modal-field-label">{t("profileModal.birthdate")}</span>
                            <input
                                className="profile-modal-field-input"
                                type="date"
                                name="birthdate"
                                value={editedProfile.birthdate}
                                onChange={handleProfileChange}
                                disabled={!editMode}
                                max={new Date().toISOString().split('T')[0]}
                                min="1900-01-01"
                            />
                        </div>
                        <div className="profile-modal-field">
                            <span className="profile-modal-field-label">{t("profileModal.country")}</span>
                            <select
                                className="profile-modal-field-input"
                                name="country"
                                value={editedProfile.country}
                                onChange={handleProfileChange}
                                disabled={!editMode}
                            >
                                {!editedProfile.country && (
                                    <option value="">{t("profileModal.selectCountry") || "Select Country"}</option>
                                )}
                                {countriesData.countries.map(country => (
                                    <option key={country.code} value={country.code}>
                                        {country.nameEn}
                                    </option>
                                ))}
                            </select>

                        </div>
                        <div className="profile-modal-field">
                            <span className="profile-modal-field-label">{t("profileModal.phone")}</span>
                            <PhoneInput
                                value={editedProfile.phoneNumber}
                                onChange={(e) => handleProfileChange({ target: { name: 'phoneNumber', value: e.target.value } })}
                                disabled={!editMode}
                                placeholder={"+7 999 999 99 99"}
                            />
                        </div>
                        
                        <div className="profile-modal-field">
                            <span className="profile-modal-field-label">{t("profile.time zone")}</span>
                            <select
                                className="profile-modal-field-input"
                                name="timezone"
                                value={editedProfile.timezone}
                                onChange={handleProfileChange}
                                disabled={!editMode}
                            >
                                {Intl.supportedValuesOf('timeZone')
                                    .map(tz => {
                                        const offset = new Date().toLocaleString('ru-RU', { timeZone: tz, timeZoneName: 'shortOffset' }).split(' ').pop();
                                        let formattedOffset;
                                        if (offset === 'GMT') {
                                            formattedOffset = 'UTC+00:00';
                                        } else {
                                            const match = offset.match(/GMT([+-])(\d+)(?::(\d+))?/);
                                            if (match) {
                                                const sign = match[1];
                                                const hours = match[2].padStart(2, '0');
                                                const minutes = match[3] ? match[3].padStart(2, '0') : '00';
                                                formattedOffset = `UTC${sign}${hours}:${minutes}`;
                                            } else {
                                                formattedOffset = offset.replace('GMT', 'UTC');
                                            }
                                        }
                                        const city = tz.split('/').pop().replace(/_/g, ' ');
                                        return {
                                            tz,
                                            offset: formattedOffset,
                                            city
                                        };
                                    })
                                    .sort((a, b) => {
                                        // Извлекаем числовое значение смещения
                                        const getOffsetValue = (offset) => {
                                            const match = offset.match(/UTC([+-])(\d{2}):(\d{2})/);
                                            if (!match) return 0;
                                            const sign = match[1] === '+' ? 1 : -1;
                                            const hours = parseInt(match[2]);
                                            const minutes = parseInt(match[3]);
                                            return sign * (hours * 60 + minutes);
                                        };
                                        return getOffsetValue(a.offset) - getOffsetValue(b.offset);
                                    })
                                    .map(({ tz, offset, city }) => (
                                        <option key={tz} value={tz}>
                                            {offset} - {city}
                                        </option>
                                    ))}
                            </select>
                        </div>

                       
                    </div>
                )}
                {editPassword && (
                    <div className="profile-modal-password-fields">
                        <div className="profile-modal-field">
                            <input
                                className="profile-modal-field-input"
                                type="password"
                                placeholder={t("profileModal.currentPassword")}
                                value={passwords.currentPassword}
                                onChange={e => setPasswords({ ...passwords, currentPassword: e.target.value })}
                            />
                        </div>
                        <div className="profile-modal-field">
                            <input
                                className="profile-modal-field-input"
                                type="password"
                                placeholder={t("profileModal.newPassword")}
                                value={passwords.newPassword}
                                onChange={e => setPasswords({ ...passwords, newPassword: e.target.value })}
                            />
                        </div>
                        <div className="profile-modal-field">
                            <input
                                className="profile-modal-field-input"
                                type="password"
                                placeholder={t("profileModal.repeatNewPassword")}
                                value={passwords.confirmPassword}
                                onChange={e => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                            />
                        </div>
                        <div className="profile-modal-password-actions">
                            <button
                                type="button"
                                className="btn sign-up glow-blue"
                                onClick={handlePasswordChange}
                                disabled={loading}
                            >
                                {loading ? t("profileModal.saving") : t("profileModal.save")}
                            </button>
                            <button
                                type="button"
                                className="btn"
                                onClick={() => { setEditPassword(false); setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" }); }}
                                disabled={loading}
                            >
                                {t("profileModal.cancel")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileModal; 